import { z } from "zod";

const optionalText = z
  .string()
  .nullish()
  .transform((value) => value ?? "");

export const authenticatedUserSchema = z.object({
  id: z.number(),
  login: z.string(),
  first_name: optionalText,
  last_name: optionalText,
  full_name: optionalText,
  abbreviation: optionalText,
  email: z.string().nullish(),
  additional_emails: z.array(z.string()).default([]),
  is_active: z.boolean(),
  is_technical: z.boolean().default(false),
  role: z.string().nullish(),
  permissions: z.array(z.string()).default([]),
});

export type AuthenticatedUser = z.infer<typeof authenticatedUserSchema>;

export const loginResponseSchema = z.object({
  token: z.string().min(1),
  user: authenticatedUserSchema.optional(),
});

export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const currentUserResponseSchema = z
  .object({ user: authenticatedUserSchema })
  .transform(({ user }) => user);

export type LoginCredentials = {
  login: string;
  password: string;
};

export type ForgotPasswordPayload = {
  login: string;
  email: string;
};

export type ChangePasswordPayload = {
  old_password: string;
  password: string;
  password_confirmation: string;
};

export const getUserHeaderName = (user: AuthenticatedUser | null): string => {
  if (!user) {
    return "";
  }

  return user.last_name.trim() || user.login;
};

export const getUserFullName = (user: AuthenticatedUser | null): string => {
  if (!user) {
    return "";
  }

  const fullName =
    user.full_name.trim() ||
    [user.first_name, user.last_name].filter(Boolean).join(" ").trim();

  return fullName || user.login;
};
